import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Github, Linkedin, Twitter, MapPin, Copy, Send, CheckCircle2 } from "lucide-react";
import { Reveal, SectionHeader } from "./shared";
import { profile } from "../../data/portfolioData";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-amber-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-amber-400/10";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", type: "", budget: "", message: "" });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Project Inquiry${form.type ? ` — ${form.type}` : ""} from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nProject type: ${form.type || "—"}\nBudget: ${form.budget || "—"}\n\n${form.message}`
    );
    window.location.href = `mailto:${profile.email}?subject=${subject}&body=${body}`;
    setSent(true);
    toast.success("Opening your email client — your message is ready to send.", {
      description: `It will be addressed to ${profile.email}`,
    });
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      toast.success("Email copied to clipboard");
    } catch {
      toast.info(profile.email);
    }
  };

  const socials = [
    { icon: Github, label: "GitHub", href: profile.socials.github, id: "github" },
    { icon: Linkedin, label: "LinkedIn", href: profile.socials.linkedin, id: "linkedin" },
    { icon: Twitter, label: "X / Twitter", href: profile.socials.twitter, id: "twitter" },
  ].filter((s) => s.href);

  return (
    <section id="contact" className="py-20 lg:py-28 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-amber-500/[0.06] blur-[130px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeader
          index="11"
          eyebrow="Initiate Contact"
          title="Let's Build Something."
          description="Have an idea, project, or business problem you'd like to discuss? Let's talk."
          testId="contact-header"
        />

        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-16">
          <Reveal>
            {sent ? (
              <div className="rounded-3xl border border-emerald-400/25 bg-emerald-400/[0.05] p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="mx-auto text-emerald-400" size={40} />
                <h3 className="mt-4 font-display text-2xl font-bold text-slate-50">Message ready to send.</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-md mx-auto">
                  Your email client should have opened with everything pre-filled. If it didn't, reach me directly at{" "}
                  <a href={`mailto:${profile.email}`} className="text-amber-400 hover:underline">{profile.email}</a>.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", email: "", type: "", budget: "", message: "" }); }}
                  className="mt-6 rounded-full border border-white/15 px-6 py-2.5 text-sm text-slate-300 hover:border-white/30 transition-colors"
                  data-testid="contact-send-another"
                >
                  Write another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-input-name" aria-label="Your name" />
                  <input required type="email" placeholder="Your email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-input-email" aria-label="Your email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <select value={form.type} onChange={set("type")} className={`${inputCls} appearance-none`} data-testid="contact-select-type" aria-label="Project type">
                    <option value="" className="bg-[#0F1420]">Project type…</option>
                    {["Full-time role / Job opportunity", "MVP development", "Mobile app", "Web application", "Backend system", "API development", "Consulting", "Something else"].map((t) => (
                      <option key={t} value={t} className="bg-[#0F1420]">{t}</option>
                    ))}
                  </select>
                  <select value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`} data-testid="contact-select-budget" aria-label="Budget (optional)">
                    <option value="" className="bg-[#0F1420]">Budget (optional)…</option>
                    {["Under $1k", "$1k – $5k", "$5k – $15k", "$15k+", "Let's discuss"].map((b) => (
                      <option key={b} value={b} className="bg-[#0F1420]">{b}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  required
                  rows={6}
                  placeholder="Tell me about your idea, project, or problem…"
                  value={form.message}
                  onChange={set("message")}
                  className={`${inputCls} resize-none`}
                  data-testid="contact-input-message"
                  aria-label="Your message"
                />
                <button
                  type="submit"
                  className="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_36px_-8px_rgba(245,158,11,0.7)]"
                  data-testid="contact-submit-button"
                >
                  Send Message
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </Reveal>

          <div className="space-y-4">
            <Reveal delay={0.1}>
              <button
                onClick={copyEmail}
                className="group w-full flex items-center justify-between rounded-2xl border border-white/[0.08] bg-[#0F1420] px-6 py-5 text-left transition-all duration-300 hover:border-amber-400/30"
                data-testid="contact-email-card"
              >
                <span className="flex items-center gap-4">
                  <span className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-3 text-amber-400"><Mail size={18} /></span>
                  <span>
                    <span className="block text-xs font-code uppercase tracking-wider text-slate-500">Email</span>
                    <span className="block text-sm font-medium text-slate-200 mt-0.5">{profile.email}</span>
                  </span>
                </span>
                <Copy size={15} className="text-slate-600 group-hover:text-amber-400 transition-colors" />
              </button>
            </Reveal>

            <Reveal delay={0.12}>
              <a
                href={`tel:${profile.phone.replace(/\s/g, "")}`}
                className="group flex items-center justify-between rounded-2xl border border-white/[0.08] bg-[#0F1420] px-6 py-5 transition-all duration-300 hover:border-white/[0.2]"
                data-testid="contact-phone-card"
              >
                <span className="flex items-center gap-4">
                  <span className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-3 text-emerald-400"><Phone size={18} /></span>
                  <span>
                    <span className="block text-xs font-code uppercase tracking-wider text-slate-500">Phone</span>
                    <span className="block text-sm font-medium text-slate-200 mt-0.5">{profile.phone}</span>
                  </span>
                </span>
                <span className="text-slate-600 group-hover:text-slate-300 group-hover:translate-x-1 transition-all">→</span>
              </a>
            </Reveal>

            {socials.map((s, i) => (
              <Reveal key={s.id} delay={0.15 + i * 0.06}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between rounded-2xl border border-white/[0.08] bg-[#0F1420] px-6 py-5 transition-all duration-300 hover:border-white/[0.2]"
                  data-testid={`contact-social-${s.id}`}
                >
                  <span className="flex items-center gap-4">
                    <span className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-3 text-slate-300 group-hover:text-amber-400 transition-colors">
                      <s.icon size={18} />
                    </span>
                    <span className="text-sm font-medium text-slate-200">{s.label}</span>
                  </span>
                  <span className="text-slate-600 group-hover:text-slate-300 group-hover:translate-x-1 transition-all">→</span>
                </a>
              </Reveal>
            ))}

            <Reveal delay={0.35}>
              <div className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#0F1420] px-6 py-5" data-testid="contact-location">
                <span className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-3 text-slate-300"><MapPin size={18} /></span>
                <span>
                  <span className="block text-xs font-code uppercase tracking-wider text-slate-500">Based in</span>
                  <span className="block text-sm font-medium text-slate-200 mt-0.5">{profile.location}</span>
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
